import type { Course } from "Models";
import type { UseDataInfo } from "QueryTypes";
import { useSelector } from "react-redux";
import { useRequest } from "redux-query-react";
import type { RootState } from "StoreTypes";
import { useMemo } from "react";
import {
  getCoursesByFacilityQuery,
  getCoursesBySimQuery,
} from "./courseQueries";

export const useSimulatorCourses = (simId?: string): UseDataInfo<Course[]> => {
  const safeQuery = useMemo(() => {
    if (!simId?.trim()) {
      console.log("simId is falsy or empty for simulator courses query");
      return null;
    }
    return getCoursesBySimQuery(simId);
  }, [simId]);

  const [queryState, refreshCourses] = useRequest(safeQuery ?? undefined);

  const simulatorCourses = useSelector(
    (state: RootState): Course[] => {
      if (!state.entities?.courses?.courseData || !simId) return [];
      const bySimId = state.entities.courses.bySimId?.[simId];
      const allCourses = Object.values(state.entities.courses.courseData);
      if (!bySimId) return [];
      return allCourses.filter((course) => bySimId.includes(course.shortName));
    },
    (left, right) => JSON.stringify(left) === JSON.stringify(right),
  );

  return [simulatorCourses, queryState, refreshCourses];
};

export const useFacilityCourses = (
  facilityId: string,
): UseDataInfo<Course[]> => {
  const safeQuery = useMemo(() => {
    if (!facilityId?.trim()) {
      console.log("facilityId is falsy or empty for facility courses query");
      return null;
    }
    return getCoursesByFacilityQuery(facilityId);
  }, [facilityId]);

  const [queryState, refreshFacilityCourses] = useRequest(
    safeQuery ?? undefined,
  );

  const allCourses = useSelector(
    (state: RootState): Course[] => {
      if (!state.entities.courses?.courseData) return [];
      // Always return the same array instance unless data changes to prevent unnecessary rerenders
      return Object.values(state.entities.courses.courseData);
    },
    // Shallow compare arrays to ensure stable references
    (left, right) =>
      left.length === right.length && left.every((c, i) => c === right[i]),
  );

  return [allCourses, queryState, refreshFacilityCourses];
};
